import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CONSENT_STORAGE_KEY, DISCLAIMER_SHORT } from "@/lib/disclaimers";

function readAck(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeAck() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function useAcademicConsent() {
  const [acknowledged, setAcknowledged] = useState(false);
  const [open, setOpen] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);

  useEffect(() => {
    setAcknowledged(readAck());
  }, []);

  const requireConsent = useCallback(
    (action: () => void) => {
      if (acknowledged || readAck()) {
        setAcknowledged(true);
        action();
        return;
      }
      pendingAction.current = action;
      setOpen(true);
    },
    [acknowledged],
  );

  const accept = useCallback(() => {
    writeAck();
    setAcknowledged(true);
    setOpen(false);
    const fn = pendingAction.current;
    pendingAction.current = null;
    if (fn) fn();
  }, []);

  const cancel = useCallback(() => {
    pendingAction.current = null;
    setOpen(false);
  }, []);

  const showWelcome = useCallback(() => {
    if (acknowledged || readAck()) return;
    setOpen(true);
  }, [acknowledged]);

  return { acknowledged, requireConsent, accept, cancel, open, setOpen, showWelcome };
}

export function AcademicConsentDialog({
  open,
  onOpenChange,
  onAccept,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onAccept: () => void;
}) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!open) setChecked(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-center">Quick acknowledgment</DialogTitle>
          <DialogDescription className="text-center">
            Before you generate or download, please confirm responsible use.
          </DialogDescription>
        </DialogHeader>

        <label
          htmlFor="academic-consent"
          className="flex cursor-pointer items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm"
        >
          <Checkbox
            id="academic-consent"
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            className="mt-0.5"
          />
          <span className="leading-relaxed text-foreground">
            {DISCLAIMER_SHORT}{" "}
            <Link
              to="/terms"
              className="text-primary underline-offset-2 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Read full Terms
            </Link>
            .
          </span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!checked} onClick={onAccept}>
            Accept & continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
